import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Building2, Phone, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { bakeryInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const EmailRegisterPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    companyType: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  const companyTypes = [
    { value: 'restaurant', label: t.register?.restaurant || 'مطعم' },
    { value: 'supermarket', label: t.register?.supermarket || 'سوبرماركت' },
    { value: 'distributor', label: t.register?.distributor || 'موزع' },
    { value: 'merchant', label: t.register?.merchant || 'تاجر' },
    { value: 'other', label: t.register?.other || 'أخرى' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = true;
    if (!formData.companyName.trim()) newErrors.companyName = true;
    if (!formData.companyType) newErrors.companyType = true;
    if (!formData.phone.trim()) newErrors.phone = true;
    if (!formData.email.trim() || !formData.email.includes('@')) newErrors.email = true;
    if (!formData.password || formData.password.length < 6) newErrors.password = true;
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = true;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // For now, just navigate to B2B dashboard (mock)
      // In production, this would call the backend API
      console.log('Form submitted:', formData);
      alert(t.register?.accountCreated || 'تم إنشاء الحساب بنجاح! سيتم مراجعة طلبك.');
      navigate('/b2b/dashboard');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative py-12" style={{ background: 'linear-gradient(135deg, #1a5c1a 0%, #2d7a2d 100%)' }}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} className="w-full h-full" />
      </div>

      <div className="relative z-10 w-full max-w-2xl mx-auto px-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/login')}
          className="mb-6 flex items-center gap-2 text-white hover:text-yellow-300 transition-colors"
          data-testid="back-to-login-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t.register?.backToOptions || 'العودة لخيارات التسجيل'}</span>
        </button>

        {/* Form Card */}
        <div className="bg-white rounded-3xl p-8 md:p-10 shadow-2xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src={bakeryInfo.logo} 
              alt="Dura Alsham"
              className="w-20 h-20 object-contain mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display', color: '#1a5c1a' }}>
              {t.register?.title || 'إنشاء حساب جديد'}
            </h1>
            <p className="text-gray-600">
              {t.register?.subtitle || 'أدخل بياناتك لإنشاء حساب تجاري'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-bold mb-2 text-gray-700">
                {t.register?.fullName || 'الاسم الكامل'}
              </label>
              <div className="relative">
                <User className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder={t.register?.fullNamePlaceholder || 'أدخل اسمك الكامل'}
                  className={`w-full pr-12 pl-4 py-4 rounded-xl border-2 transition-colors focus:outline-none ${
                    errors.fullName ? 'border-red-500' : 'border-gray-200 focus:border-green-600'
                  }`}
                  data-testid="fullname-input"
                />
              </div>
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm font-bold mb-2 text-gray-700">
                {t.register?.companyName || 'اسم الشركة / المؤسسة'}
              </label>
              <div className="relative">
                <Building2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder={t.register?.companyNamePlaceholder || 'أدخل اسم شركتك'}
                  className={`w-full pr-12 pl-4 py-4 rounded-xl border-2 transition-colors focus:outline-none ${
                    errors.companyName ? 'border-red-500' : 'border-gray-200 focus:border-green-600'
                  }`}
                  data-testid="company-name-input"
                />
              </div>
            </div>

            {/* Company Type */}
            <div>
              <label className="block text-sm font-bold mb-2 text-gray-700">
                {t.register?.companyType || 'نوع النشاط التجاري'}
              </label>
              <select
                name="companyType"
                value={formData.companyType}
                onChange={handleChange}
                className={`w-full px-4 py-4 rounded-xl border-2 transition-colors focus:outline-none appearance-none bg-white ${
                  errors.companyType ? 'border-red-500' : 'border-gray-200 focus:border-green-600'
                }`}
                data-testid="company-type-select"
              >
                <option value="">{t.register?.selectCompanyType || 'اختر نوع النشاط'}</option>
                {companyTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-bold mb-2 text-gray-700">
                {t.register?.phone || 'رقم الهاتف'}
              </label>
              <div className="relative">
                <Phone className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t.register?.phonePlaceholder || 'أدخل رقم هاتفك'}
                  className={`w-full pr-12 pl-4 py-4 rounded-xl border-2 transition-colors focus:outline-none ${
                    errors.phone ? 'border-red-500' : 'border-gray-200 focus:border-green-600'
                  }`}
                  dir="ltr"
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-bold mb-2 text-gray-700">
                {t.register?.email || 'البريد الإلكتروني'}
              </label>
              <div className="relative">
                <Mail className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.register?.emailPlaceholder || 'أدخل بريدك الإلكتروني'}
                  className={`w-full pr-12 pl-4 py-4 rounded-xl border-2 transition-colors focus:outline-none ${
                    errors.email ? 'border-red-500' : 'border-gray-200 focus:border-green-600'
                  }`}
                  dir="ltr"
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-bold mb-2 text-gray-700">
                {t.register?.password || 'كلمة المرور'}
              </label>
              <div className="relative">
                <Lock className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t.register?.passwordPlaceholder || 'أدخل كلمة المرور'}
                  className={`w-full pr-12 pl-12 py-4 rounded-xl border-2 transition-colors focus:outline-none ${
                    errors.password ? 'border-red-500' : 'border-gray-200 focus:border-green-600'
                  }`}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-bold mb-2 text-gray-700">
                {t.register?.confirmPassword || 'تأكيد كلمة المرور'}
              </label>
              <div className="relative">
                <Lock className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder={t.register?.confirmPasswordPlaceholder || 'أعد إدخال كلمة المرور'}
                  className={`w-full pr-12 pl-12 py-4 rounded-xl border-2 transition-colors focus:outline-none ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-200 focus:border-green-600'
                  }`}
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full py-4 rounded-xl font-bold text-white text-lg transition-all duration-300 hover:scale-105 shadow-lg mt-6"
              style={{ background: 'linear-gradient(135deg, #1a5c1a 0%, #2d7a2d 100%)' }}
              data-testid="create-account-btn"
            >
              {t.register?.createAccount || 'إنشاء الحساب'}
            </button>
          </form>

          {/* Login Link */}
          <p className="text-center text-gray-600 mt-6">
            {t.register?.haveAccountLogin || 'لديك حساب؟'}{' '}
            <button 
              onClick={() => navigate('/login')}
              className="text-green-700 font-bold hover:underline"
              data-testid="login-link"
            >
              {t.register?.loginHere || 'سجل دخول من هنا'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailRegisterPage;
